/**
 * AppleSignInButton — bottone "Accedi con Apple" visibile SOLO su iOS
 * nativo (Capacitor). Su web/Android non viene renderizzato.
 *
 * iter v29 r168 — App Store Settimana 2.
 * Apple guideline 4.8 OBBLIGA Sign in with Apple se l'app offre altri
 * social login (Google nel nostro caso). Senza questo la build viene
 * rifiutata in review.
 *
 * Flow:
 *   1. nativeAppleSignIn() → SignInWithApple plugin → identityToken
 *   2. POST /api/auth/apple { id_token, nonce, full_name? }
 *   3. saveJwt(data.access_token) + setUser + navigate
 */
import React, { useState } from "react";
import { AppleLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { isIOS, nativeAppleSignIn, nativeHaptic } from "@/lib/native";
import { api, saveJwt } from "@/lib/api";

export default function AppleSignInButton({ onAuthenticated, onSetUser }) {
  const [loading, setLoading] = useState(false);

  // Web → niente bottone. Apple non vuole il "fake" button su browser per
  // un'app distribuita via App Store, e su Android non ha senso.
  if (!isIOS) return null;

  const handle = async () => {
    // iter v29 r201 — Telemetria visibile su TestFlight: l'utente vede subito
    // che il tap è stato registrato. Se questo toast non appare, il problema
    // è prima del click handler (overlay, z-index, JS error).
    toast.info("Apertura accesso Apple…", { duration: 1500 });
    nativeHaptic("light").catch(() => {});
    setLoading(true);
    try {
      const apple = await nativeAppleSignIn();
      if (!apple?.idToken) {
        toast.error("Apple non ha restituito il token.");
        return;
      }
      const { data } = await api.post("/auth/apple", {
        id_token: apple.idToken,
        nonce: apple.nonce,
        full_name: apple.fullName || undefined,
      });
      // saveJwt accetta solo la stringa token. Il backend torna `access_token`.
      const token = data?.access_token || data?.token;
      if (!token) throw new Error("Token JWT mancante dalla risposta server");
      saveJwt(token);
      onSetUser?.(data.user);
      onAuthenticated?.(data.user);
      toast.success("Bentornato.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Errore con Apple";
      const msgStr = String(msg);
      // iter v31 r16 — AuthorizationError 1001 = "user canceled" (utente
      // ha chiuso il foglio Apple). NON mostrare un errore rosso, è
      // un'azione volontaria. Mostriamo solo errori veri (1000 = entitlement
      // mancante, 1004 = failed, ecc.).
      const isUserCancel =
        msgStr.includes("1001") ||
        /cancel/i.test(msgStr) ||
        msgStr.includes("ASAuthorizationError.canceled");
      if (!isUserCancel) {
        toast.error(`Apple: ${msgStr}`, { duration: 6000 });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handle}
      disabled={loading}
      className="w-full flex items-center justify-center gap-3 bg-[#1C1C1A] hover:bg-[#000] text-white px-4 py-3 rounded-sm transition-colors disabled:opacity-60"
      data-testid="apple-signin-btn"
    >
      <AppleLogo size={20} weight="fill" />
      <span className="font-medium">{loading ? "Accesso…" : "Accedi con Apple"}</span>
    </button>
  );
}
